import * as React from 'react'
import { useParams } from 'react-router-dom'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { CreditCard, Landmark } from 'lucide-react'
import type { PublicPaymentDto } from '@lib/dto/sales-payment.dto'
import type { OnlinePaymentMethod } from '@lib/enums/sales-payment.enum'
import { dollarsFromCents } from '@lib/helpers/format'
import { paymentIntentInputSchema } from '@lib/input/sales-payment.input'
import { useAuth } from '@/auth/AuthContext'
import { PublicShell } from '@/components/public/PublicShell'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Checkbox } from '@/components/ui/checkbox'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { LoadingState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { usePublicPayment, useStartPayment } from '@/hooks/useSales'
import { formatEventDate } from '@/lib/eventTime'

/** "Paid on Oct 21, 2026" from an instant, in the payer's own day. */
function paidOn(at: string): string {
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Stripe appends this when a bank or card check sends the browser away and back. */
export function stripeRedirectStatus(search: string): 'succeeded' | 'processing' | 'failed' | null {
  const status = new URLSearchParams(search).get('redirect_status')
  if (status === 'succeeded' || status === 'processing' || status === 'failed') return status
  return null
}

/** What confirmPayment settled to, when it settled without leaving the page. */
type ConfirmedPayment = 'succeeded' | 'processing'

/** Stripe's form for the payment, and the button that confirms it. */
function ConfirmForm({ chargeCents, onPaid }: { chargeCents: number; onPaid: (status: ConfirmedPayment) => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handlePay(): Promise<void> {
    if (stripe === null || elements === null) return
    setBusy(true)
    setError(null)
    try {
      const result = await stripe.confirmPayment({ elements, redirect: 'if_required', confirmParams: { return_url: window.location.href } })
      if (result.error) {
        setError(result.error.message ?? 'That didn’t go through. Check the details and try again.')
        return
      }
      const status = result.paymentIntent?.status
      if (status === 'succeeded' || status === 'processing') {
        onPaid(status)
        return
      }
      setError('That didn’t go through. Check the details and try again.')
    } catch (cause) {
      setError(requestErrorMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <PaymentElement />
      {error === null ? null : <FormError>{error}</FormError>}
      <Button className="w-full" onClick={() => void handlePay()} disabled={busy || stripe === null}>
        {busy ? 'Paying…' : `Pay ${dollarsFromCents(chargeCents)}`}
      </Button>
    </div>
  )
}

function Payment({ token, payment, onPaid }: { token: string; payment: PublicPaymentDto; onPaid: (status: ConfirmedPayment) => void }) {
  const { user } = useAuth()
  const start = useStartPayment(token)
  const [method, setMethod] = React.useState<OnlinePaymentMethod>('card')
  const [name, setName] = React.useState(user?.name ?? '')
  const [email, setEmail] = React.useState(user?.email ?? '')
  const [saveForLater, setSaveForLater] = React.useState(false)
  // Saving a way to pay is for the couple, signed in, paying the deposit, or a later payment once autopay was turned off.
  const canSave = payment.canSaveForLater && user?.role === 'CLIENT'
  const [error, setError] = React.useState<string | null>(null)
  const [intent, setIntent] = React.useState<{ clientSecret: string; chargeCents: number } | null>(null)
  const stripe = React.useMemo<Promise<Stripe | null>>(() => loadStripe(payment.publishableKey, { stripeAccount: payment.businessAccountId }), [payment.publishableKey, payment.businessAccountId])

  function handleContinue(): void {
    const parsed = paymentIntentInputSchema.safeParse({ method, payerName: name, payerEmail: email, ...(canSave && saveForLater ? { saveForLater: true } : {}) })
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Fill in your name and email.')
    setError(null)
    start.mutate(parsed.data, { onSuccess: setIntent, onError: (e) => setError(requestErrorMessage(e)) })
  }

  if (intent !== null) {
    return (
      <Elements stripe={stripe} options={{ clientSecret: intent.clientSecret }}>
        <ConfirmForm chargeCents={intent.chargeCents} onPaid={onPaid} />
      </Elements>
    )
  }

  return (
    <div className="space-y-6">
      {payment.bankChargeCents === null ? null : (
        <ChoiceGroup legend="How you’ll pay" hideLegend columns={2}>
          <ChoiceCard
            type="radio"
            name="payment-method"
            checked={method === 'card'}
            onSelect={() => setMethod('card')}
            media={<CreditCard aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" />}
            title={`Card: ${dollarsFromCents(payment.amountCents)}`}
            description="Credit or debit card."
          />
          <ChoiceCard
            type="radio"
            name="payment-method"
            checked={method === 'bank'}
            onSelect={() => setMethod('bank')}
            media={<Landmark aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" />}
            title={`Bank transfer: ${dollarsFromCents(payment.bankChargeCents)}`}
            description={`Save ${dollarsFromCents(payment.bankDiscountCents)}. Sign in to your bank; it takes a few days to arrive.`}
          />
        </ChoiceGroup>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Your name" htmlFor="payer-name">
          <Input id="payer-name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email for your receipt" htmlFor="payer-email">
          <Input id="payer-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
      {canSave ? (
        <label className="flex items-start gap-2 text-sm text-ink">
          <Checkbox checked={saveForLater} onCheckedChange={setSaveForLater} className="mt-0.5" />
          <span>
            Pay later payments automatically with this {method === 'bank' ? 'account' : 'card'}.
            <span className="block text-ink-muted">You’re emailed three days before each, and can turn it off any time.</span>
          </span>
        </label>
      ) : null}
      {error === null ? null : <FormError>{error}</FormError>}
      <Button className="w-full" onClick={handleContinue} disabled={start.isPending}>
        {start.isPending ? 'One moment…' : 'Continue to payment'}
      </Button>
    </div>
  )
}

/** Why the payment can't be shown: an old link, or paying online not set up yet. */
function PayProblem({ error }: { error: Error }) {
  const known = isApiError(error) && error.body.message !== undefined
  return (
    <PublicShell>
      <Callout tone="attention" title={known ? requestErrorMessage(error) : 'This page could not be loaded.'}>
        {known && error.status === 404 ? 'Ask the venue for the current link.' : null}
        {known ? null : 'Check your connection and try again.'}
      </Callout>
    </PublicShell>
  )
}

/**
 * A payment's link, `/pay/:token` (docs/sales.spec.md, "Paying"): whoever has
 * it can pay, a parent with no account included. It shows the venue, what
 * the payment is for and what's left, then Stripe's form; afterwards it waits
 * for Stripe's word that the money arrived.
 */
export function PayPage(): React.ReactElement {
  const { token = '' } = useParams()
  // Read before the effect below removes Stripe's query. A card check that
  // left the page comes back as a fresh load, and ignoring that status showed
  // the form again after the money had already moved.
  const stripeReturn = React.useMemo(() => stripeRedirectStatus(window.location.search), [])
  const [awaiting, setAwaiting] = React.useState<ConfirmedPayment | null>(
    stripeReturn === 'succeeded' || stripeReturn === 'processing' ? stripeReturn : null,
  )
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('payment_intent') && !params.has('payment_intent_client_secret') && !params.has('redirect_status')) return
    window.history.replaceState(null, '', window.location.pathname)
  }, [])
  const query = usePublicPayment(token, awaiting !== null)

  if (query.isPending) {
    return (
      <PublicShell>
        <LoadingState label="Loading the payment…" />
      </PublicShell>
    )
  }
  if (query.isError) return <PayProblem error={query.error} />

  const payment = query.data
  return (
    <PublicShell venueName={payment.venueName}>
      <div className="space-y-1">
        <h1 className="m-0 text-2xl font-semibold text-ink">{payment.label}</h1>
        <p className="m-0 text-sm text-ink-muted">
          For the event on {formatEventDate(payment.eventDate)}
          {payment.dueOn === null ? '' : `, due ${formatEventDate(payment.dueOn)}`}.
        </p>
      </div>
      {payment.status === 'paid' ? (
        <Callout tone="success" title={payment.paidAt === null ? 'Paid. Thank you.' : `Paid on ${paidOn(payment.paidAt)}. Thank you.`}>
          A receipt went to the email given when paying.
        </Callout>
      ) : null}
      {payment.status !== 'paid' && (awaiting !== null || payment.status === 'processing') ? (
        <Callout tone="success" title={`Thanks. ${payment.venueName} will see your payment.`}>
          {payment.status === 'processing' || awaiting === 'processing' || stripeReturn === 'processing'
            ? 'A bank transfer takes a few days to arrive. You’ll get a receipt by email when it does.'
            : 'You’ll get a receipt by email in a moment.'}
        </Callout>
      ) : null}
      {payment.status !== 'paid' && payment.status !== 'processing' && awaiting === null ? (
        <>
          {stripeReturn === 'failed' ? <FormError>The payment didn’t go through. You can try again.</FormError> : null}
          <p className="m-0 text-3xl font-semibold tabular-nums text-ink">{dollarsFromCents(payment.amountCents)}</p>
          <Payment token={token} payment={payment} onPaid={setAwaiting} />
        </>
      ) : null}
    </PublicShell>
  )
}
